import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ImagePlus, Trash2, X } from 'lucide-react';
import { Spinner } from '@/components/ui';
import { toast } from '@/components/feedback/toast';
import { cn } from '@/lib/utils';
import { useAuth } from '@/app/providers';
import type { ThemeAsset } from '@/types';
import { uploadThemeAsset, publicAssetUrl } from '../api/assets';
import { deleteAsset, listMyAssets } from '../api/profileStudio';

const assetKeys = { mine: (uid: string) => ['theme-assets', uid] as const };

/**
 * Your uploaded asset library — real `theme_assets` rows in the theme-assets
 * bucket. Click an image to drop it into the banner as a new layer (or fill
 * the selected empty image layer); uploads run through the shared optimizer.
 */
export function AssetsPanel({ onClose, onPlace }: { onClose: () => void; onPlace: (url: string) => void }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const { data: assets = [], isLoading } = useQuery({
    queryKey: assetKeys.mine(user?.id ?? ''),
    queryFn: () => listMyAssets(user!.id),
    enabled: Boolean(user),
  });

  const del = useMutation({
    mutationFn: (a: ThemeAsset) => deleteAsset(a.id, a.path),
    onSuccess: () => void qc.invalidateQueries({ queryKey: assetKeys.mine(user?.id ?? '') }),
    onError: (e) => toast((e as Error).message || 'Could not delete asset', 'error'),
  });

  async function onUpload(file: File): Promise<void> {
    if (!user) return;
    setUploading(true);
    try {
      await uploadThemeAsset(file, 'banner', user.id);
      void qc.invalidateQueries({ queryKey: assetKeys.mine(user.id) });
      toast('Asset uploaded', 'ok');
    } catch (e) {
      toast((e as Error).message || 'Upload failed', 'error');
    } finally {
      setUploading(false);
    }
  }

  function place(asset: ThemeAsset): void {
    onPlace(publicAssetUrl(asset.path));
    onClose();
  }

  return (
    <div className="absolute bottom-3 right-3 top-14 z-30 flex w-80 flex-col overflow-hidden rounded-xl border border-white/12 bg-ink-850 shadow-[0_24px_60px_rgba(0,0,0,0.6)]">
      <div className="flex flex-none items-center justify-between border-b border-white/[0.06] px-4 py-3">
        <span className="flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-[0.7px] text-lo">
          <ImagePlus size={13} strokeWidth={2.4} /> Assets
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close assets"
          className="grid h-6 w-6 place-items-center rounded text-mid transition-colors hover:bg-white/10 hover:text-hi"
        >
          <X size={14} />
        </button>
      </div>

      <div className="flex-none p-3 pb-1">
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="sr-only"
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = '';
            if (f) void onUpload(f);
          }}
        />
        <button
          type="button"
          disabled={uploading || !user}
          onClick={() => fileRef.current?.click()}
          className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-white/15 py-2.5 text-[12px] font-bold text-mid transition-colors hover:border-white/30 hover:text-hi disabled:opacity-50"
        >
          <ImagePlus size={14} /> {uploading ? 'Uploading…' : 'Upload image'}
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-3">
        {isLoading ? (
          <div className="grid place-items-center py-8">
            <Spinner />
          </div>
        ) : assets.length === 0 ? (
          <p className="px-1 py-4 text-[11.5px] leading-relaxed text-white/35">
            Nothing here yet — upload images to use in your banners and backgrounds.
          </p>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            {assets.map((a) => (
              <div key={a.id} className="group relative overflow-hidden rounded-lg border border-white/10">
                <button
                  type="button"
                  title="Add to banner"
                  onClick={() => place(a)}
                  className="block aspect-video w-full bg-cover bg-center transition-transform duration-200 group-hover:scale-105"
                  style={{ backgroundImage: `url(${publicAssetUrl(a.path)})` }}
                />
                <button
                  type="button"
                  title="Delete asset"
                  aria-label="Delete asset"
                  onClick={() => {
                    if (window.confirm('Delete this asset? Banners using it will show an empty layer.')) {
                      del.mutate(a);
                    }
                  }}
                  className={cn(
                    'absolute right-1 top-1 grid h-6 w-6 place-items-center rounded-md bg-black/60 text-white/60 opacity-0 transition-opacity hover:text-[#ff8e8e] group-hover:opacity-100',
                  )}
                >
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
